import React from "react";
import { cachedGet } from "@/lib/refCache";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Session 38 — shared helpers for the Inventory → Costing → Profit panels
 * (Variance, Waste, Receiving, Cost Coverage). Pure presentation helpers;
 * every number comes from the backend ledger endpoints.
 */

export const R = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const neg = Number(n) < 0;
  const [whole, decimal] = Math.abs(Number(n)).toFixed(2).split(".");
  return `${neg ? "−" : ""}R ${Number(whole).toLocaleString("en-US")}.${decimal}`;
};

export const num = (v, fallback = 0) => (Number.isFinite(Number(v)) ? Number(v) : fallback);

export const fmtQty = (v) => {
  if (v === null || v === undefined) return "—";
  const n = num(v);
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(3).replace(/\.?0+$/, "");
};

export const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);

export const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const RANGE_PRESETS = [
  { v: "1d", label: "Today" },
  { v: "yesterday", label: "Yesterday" },
  { v: "7d", label: "Last 7 days" },
  { v: "30d", label: "Last 30 days" },
];

export function rangeToDates(key) {
  const today = new Date();
  const end = isoDate(today);
  if (key === "1d") return { start_date: end, end_date: end };
  if (key === "yesterday") {
    const d = new Date(today);
    d.setDate(d.getDate() - 1);
    const y = isoDate(d);
    return { start_date: y, end_date: y };
  }
  if (key === "7d") {
    const d = new Date(today);
    d.setDate(d.getDate() - 6);
    return { start_date: isoDate(d), end_date: end };
  }
  if (key === "30d") {
    const d = new Date(today);
    d.setDate(d.getDate() - 29);
    return { start_date: isoDate(d), end_date: end };
  }
  return { start_date: end, end_date: end };
}

export const RangePicker = ({ value, onChange, testIdPrefix, extra }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid={`${testIdPrefix}-range-picker`}>
    {RANGE_PRESETS.map((r) => (
      <Button
        key={r.v}
        type="button"
        size="sm"
        variant={value === r.v ? "default" : "outline"}
        className="rounded-xl"
        onClick={() => onChange(r.v)}
        data-testid={`${testIdPrefix}-range-${r.v}`}
      >
        {r.label}
      </Button>
    ))}
    {extra}
  </div>
);

export const StatTile = ({ label, value, hint, tone = "default", testId }) => {
  const toneCls = {
    default: "border-border bg-card",
    good: "border-emerald-500/40 bg-emerald-500/10",
    warn: "border-amber-500/40 bg-amber-500/10",
    bad: "border-destructive/40 bg-destructive/10",
    muted: "border-border bg-secondary/40",
  }[tone] || "border-border bg-card";
  return (
    <Card className={`rounded-2xl ${toneCls}`} data-testid={testId}>
      <CardContent className="p-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 text-xl font-semibold tabular-nums text-foreground">{value}</p>
        {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
};

export const ExplainerBox = ({ title, children, testId }) => (
  <div className="rounded-2xl border border-border bg-secondary/40 p-3 text-sm text-muted-foreground" data-testid={testId}>
    <p className="font-medium text-foreground">{title}</p>
    <div className="mt-1 space-y-1">{children}</div>
  </div>
);

export const EmptyState = ({ title, body, action, testId }) => (
  <div className="rounded-2xl border border-dashed border-border p-6 text-center" data-testid={testId}>
    <p className="text-sm font-medium text-foreground">{title}</p>
    {body ? <p className="mt-1 text-xs text-muted-foreground">{body}</p> : null}
    {action ? <div className="mt-3 flex justify-center">{action}</div> : null}
  </div>
);

export const ErrorState = ({ message, onRetry, testId }) => (
  <div className="rounded-2xl border border-destructive/40 bg-destructive/10 p-4" data-testid={testId}>
    <p className="text-sm font-medium text-foreground">Couldn't load this view</p>
    <p className="mt-1 text-xs text-muted-foreground">{message}</p>
    {onRetry ? <Button type="button" size="sm" variant="outline" className="mt-3 rounded-xl" onClick={onRetry} data-testid={`${testId}-retry`}>Try again</Button> : null}
  </div>
);

export const errMsg = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (!d) return error?.message || fallback;
  if (typeof d === "string") return d;
  if (d.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join("; ");
  return fallback;
};

export const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-ZA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

export const humanize = (s) => String(s || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

// ── Session 39 — stock locations ──────────────────────────────────────────
export const LOCATION_STATE_TONE = {
  ok: "border-border bg-secondary/40 text-foreground",
  low: "border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300",
  out: "border-border bg-muted text-muted-foreground",
  negative: "border-destructive/40 bg-destructive/10 text-destructive",
};

export const CONSUMPTION_LABEL = { normal: "Normal", fallback: "Fallback", shortfall: "Shortfall", restore: "Restored" };
export const CONSUMPTION_TONE = {
  normal: "border-border text-muted-foreground",
  fallback: "border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-300",
  shortfall: "border-destructive/50 bg-destructive/10 text-destructive",
  restore: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
};

/** Fetches /owner/stock-locations once (and on demand). Location values sent
 *  to the API are location IDs; the server still accepts the legacy keys. */
export const useStockLocations = (http, authHeaders) => {
  const [data, setData] = React.useState({ locations: [], settings: null, default_priority: [] });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [fromCache, setFromCache] = React.useState(false);
  const reload = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await cachedGet(http, "/owner/stock-locations", { headers: authHeaders });
      setData(r.data || { locations: [], settings: null, default_priority: [] });
      setFromCache(!!r.fromCache);
    } catch (e) {
      setError(errMsg(e, "Unable to load stock locations."));
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders]);
  // Mount-only load (pickers/range changes call load() themselves).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { reload(); }, []);
  const active = React.useMemo(() => (data.locations || []).filter((l) => l.active !== false), [data.locations]);
  const byId = React.useMemo(() => Object.fromEntries((data.locations || []).map((l) => [l.id, l])), [data.locations]);
  const byKey = React.useMemo(() => Object.fromEntries((data.locations || []).filter((l) => l.key).map((l) => [l.key, l])), [data.locations]);
  return { ...data, active, byId, byKey, loading, error, fromCache, reload };
};

export const locationLabel = (locs, idOrKey) => {
  if (!idOrKey) return "—";
  const l = (locs || []).find((x) => x.id === idOrKey || x.key === idOrKey);
  return l ? l.name : idOrKey === "floor" ? "Floor / Kitchen" : idOrKey === "back_of_house" ? "Back of house" : String(idOrKey);
};
